import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const switchVariants = cva(
  // Track geometry derives from the density control heights (the same knob
  // button.tsx rides). Each size sets --switch-height; width and the thumb
  // travel are computed from it so the proportions hold at every density.
  // At `comfortable` density: sm=20px, default=24px (the classic h-6 w-11),
  // lg=28px; `touch` lifts default to a 36px-tall gloved-hand track.
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors h-[var(--switch-height)] w-[var(--switch-width)] [--switch-width:calc(var(--switch-height)*1.8333)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
  {
    variants: {
      size: {
        sm: '[--switch-height:calc(var(--control-height-sm)*0.625)]',
        default: '[--switch-height:calc(var(--control-height-sm)*0.75)]',
        lg: '[--switch-height:calc(var(--control-height-md)*0.7)]',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> &
    VariantProps<typeof switchVariants>
>(({ className, size, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(switchVariants({ size }), className)}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        // Thumb fills the track minus the 2px border on each side; checked
        // travel is the track width minus the (square) thumb + borders.
        'pointer-events-none block size-[calc(var(--switch-height)-4px)] rounded-full bg-background shadow-[var(--shadow-sm)] ring-0 transition-transform data-[state=checked]:translate-x-[calc(var(--switch-width)-var(--switch-height))] data-[state=unchecked]:translate-x-0'
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch, switchVariants };
